import {Component, computed, HostListener, input, OnInit, signal} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import {Divider} from 'primeng/divider';
import {toSignal} from '@angular/core/rxjs-interop';
import {startWith, switchMap} from 'rxjs';
import {toObservable} from '@angular/core/rxjs-interop';
import {PartyType} from '../../../../../api';
import {ElciaCard} from '../../../../components/card/card';
import {PartyDropdown} from '../../../../components/party-dropdown/party-dropdown';
import {ElciaDatePicker} from '../../../../components/date-picker/date-picker';
import {ElciaButton} from '../../../../components/buttons/button/button';
import {LegalEntityDropdown} from '../legal-entity-dropdown/legal-entity-dropdown';
import {CreditNoteLineForm} from '../credit-note-line-form/credit-note-line-form';
import {AmountRecap, InvoiceLineAmount} from '../tax-recap/amount-recap.component';
import {CreditNoteLineModelForm, CreditNoteModelForm} from '../credit-note-form/credit-note-form';

@Component({
  selector: 'elcia-credit-note-edit',
  imports: [
    TranslatePipe,
    Divider,
    ElciaCard,
    PartyDropdown,
    ElciaDatePicker,
    ElciaButton,
    LegalEntityDropdown,
    CreditNoteLineForm,
    AmountRecap,
  ],
  templateUrl: './credit-note-edit.html',
  styleUrl: './credit-note-edit.scss',
})
export class CreditNoteEdit implements OnInit {
  readonly fg = input.required<FormGroup<CreditNoteModelForm>>();
  // When a credit note is linked to an invoice it must credit it in full: the lines are prefilled
  // from the invoice and the user only adjusts the header, so line editing is locked.
  readonly linkedToInvoice = input<boolean>(false);

  private readonly fg$ = toObservable(this.fg);

  protected readonly partyTypes = signal<PartyType[]>([PartyType.Contact, PartyType.Company]);

  protected readonly legalEntityIdValue = this.controlValue(fg => fg.controls.legalEntityId);
  protected readonly customerIdValue = this.controlValue(fg => fg.controls.customerId);
  protected readonly creditNoteDateValue = this.controlValue(fg => fg.controls.creditNoteDate);

  protected readonly canEditLines = computed(() =>
    this.legalEntityIdValue() != null && this.customerIdValue() != null,
  );

  private readonly linesValue = toSignal(
    this.fg$.pipe(switchMap(fg =>
      fg.controls.lines.valueChanges.pipe(startWith(fg.controls.lines.value)),
    )),
  );
  protected readonly lineRecap = computed(() => this.linesValue()?.map(l => ({
    quantity: l.quantity,
    unitPrice: l.unitPrice,
    taxId: l.taxId,
  } as InvoiceLineAmount)) ?? []);

  ngOnInit() {
    if (this.fg().controls.lines.length === 0 && !this.linkedToInvoice()) {
      this.addLine();
    }
  }

  @HostListener('window:keydown.alt.+')
  protected addLine() {
    if (this.linkedToInvoice()) return;
    this.fg().controls.lines.push(this.createLineForm());
  }

  protected removeLine(index: number) {
    this.fg().controls.lines.removeAt(index);
  }

  private createLineForm() {
    return new FormGroup<CreditNoteLineModelForm>({
      id: new FormControl(null),
      productId: new FormControl<number | null>(null, Validators.required),
      label: new FormControl(null, Validators.required),
      description: new FormControl(null),
      quantity: new FormControl(null, Validators.required),
      unitPrice: new FormControl(null, Validators.required),
      taxId: new FormControl(null, Validators.required),
    });
  }

  private controlValue<T>(selector: (fg: FormGroup<CreditNoteModelForm>) => FormControl<T>) {
    return toSignal(
      this.fg$.pipe(switchMap(fg => {
        const control = selector(fg);
        return control.valueChanges.pipe(startWith(control.value));
      })),
      {initialValue: null},
    );
  }
}
